import { WorkspaceStatus } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { useListDeployments, useListTenants, useListWorkspaces } from '@sunstone/api-client/hooks';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthorization, useAuthToken } from '@/features/auth';
import { AtRiskWorkspaces } from '@/features/dashboard/AtRiskWorkspaces';
import { FleetPulse } from '@/features/dashboard/FleetPulse';
import { ReleaseEvents } from '@/features/dashboard/ReleaseEvents';
import { SystemHealthWidget } from '@/features/dashboard/SystemHealthWidget';
import { WorkspaceSummaryCards } from '@/features/dashboard/WorkspaceSummaryCards';

export function DashboardPage() {
  const { token } = useAuthToken();
  const { isPlatformAdmin, scope, tenantId } = useAuthorization();

  const isPartner = scope === 'partner';
  const isTenant = scope === 'tenant';

  // ── Count-only queries (pageSize:1, discard workspace objects) ─────────────
  const { data: totalData, isLoading: totalLoading } = useListWorkspaces(undefined, token, {
    pageSize: 1,
  });
  const { data: healthyData, isLoading: healthyLoading } = useListWorkspaces(undefined, token, {
    pageSize: 1,
    statusFilter: WorkspaceStatus.HEALTHY,
  });

  // ── At-risk workspace queries — exhaustive, not capped by creation date ────
  const { data: degradedData, isLoading: degradedLoading } = useListWorkspaces(undefined, token, {
    pageSize: 20,
    statusFilter: WorkspaceStatus.DEGRADED,
  });
  const { data: unavailableData, isLoading: unavailableLoading } = useListWorkspaces(
    undefined,
    token,
    { pageSize: 20, statusFilter: WorkspaceStatus.UNAVAILABLE },
  );
  const { data: blockedData, isLoading: blockedLoading } = useListWorkspaces(undefined, token, {
    pageSize: 20,
    statusFilter: WorkspaceStatus.BLOCKED,
  });
  const { data: provisioningFailedData, isLoading: provisioningFailedLoading } = useListWorkspaces(
    undefined,
    token,
    { pageSize: 20, statusFilter: WorkspaceStatus.PROVISIONING_FAILED },
  );

  // ── Deployments: 20 most recent for Fleet Pulse; totalCount from same response ─
  const { data: deploymentsData, isLoading: deploymentsLoading } = useListDeployments(
    token,
    isTenant ? tenantId : undefined,
    isPartner ? tenantId : undefined,
    { pageSize: 20 },
  );

  const { data: tenantsData } = useListTenants(
    token,
    isPartner ? tenantId : undefined,
    { pageSize: 1 },
    !isTenant,
  );

  const isLoading = totalLoading || healthyLoading || deploymentsLoading;

  const totalWorkspaces = totalData?.totalCount ?? 0;
  const healthyWorkspaces = healthyData?.totalCount ?? 0;
  const atRiskWorkspaces = [
    ...(degradedData?.workspaces ?? []),
    ...(unavailableData?.workspaces ?? []),
    ...(blockedData?.workspaces ?? []),
    ...(provisioningFailedData?.workspaces ?? []),
  ];
  const atRiskCount =
    (degradedData?.totalCount ?? 0) +
    (unavailableData?.totalCount ?? 0) +
    (blockedData?.totalCount ?? 0) +
    (provisioningFailedData?.totalCount ?? 0);
  const atRiskLoading =
    degradedLoading || unavailableLoading || blockedLoading || provisioningFailedLoading;

  const deployments = deploymentsData?.deployments ?? [];

  const headerDescription = isTenant
    ? 'Overview of your deployments and workspaces.'
    : isPartner
      ? 'Overview of your customers, deployments, and workspaces.'
      : 'Monitor customer health, deployment counts, and live API status.';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader title="Overview" description={headerDescription} />

      {/* Deployment-level fleet map — no workspace pagination concern */}
      <FleetPulse
        deployments={deployments}
        isLoading={deploymentsLoading}
        totalDeploymentCount={deploymentsData?.totalCount}
      />

      {/* Accurate counts from targeted queries */}
      <WorkspaceSummaryCards
        totalWorkspaces={totalWorkspaces}
        healthyWorkspaces={healthyWorkspaces}
        atRiskCount={atRiskCount}
        deploymentCount={deploymentsData?.totalCount ?? 0}
        tenantCount={tenantsData?.totalCount}
        isLoading={isLoading}
        atRiskLoading={atRiskLoading}
        showTenants={scope === 'all' || scope === 'partner'}
        isPlatformAdmin={isPlatformAdmin}
      />

      <div className="grid gap-6 grid-cols-1 md:grid-cols-3 items-stretch">
        {/* At-risk list is exhaustive — status-filtered, not age-capped */}
        <AtRiskWorkspaces
          workspaces={atRiskWorkspaces}
          totalCount={atRiskCount}
          isLoading={atRiskLoading}
        />
        <SystemHealthWidget />
        {/* audit_events has no tenant scoping today — every role sees release events
            across all tenants, not just their own. Accepted tradeoff for now. */}
        <ReleaseEvents />
      </div>
    </div>
  );
}
